import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { AlertCircle, Check, CheckCheck, Clock } from 'lucide-react'
import type { ReactNode } from 'react'
import { ChannelIcon } from 'v2/shared/components/channel-icons'
import type { Channels } from 'v2/types/api/conversations'
import { ChunkState } from 'v2/types/chunk.internal'
import dayjs from '../../../../utils/dayjs'
import { getAgentTimezone, getProperTimetoken } from '../../utils'

interface ChunkFooterProps {
  timetoken: number
  state?: ChunkState
  className?: string
  align?: 'left' | 'right'
  channel?: Channels
  text1?: ReactNode
  stateReason?: string // This is the reason for the state
}

const stateIcon = {
  [ChunkState.PENDING]: <Clock className="w-3 h-3" />,
  [ChunkState.SENT]: <Check className="w-3 h-3" />,
  [ChunkState.DELIVERED]: <CheckCheck className="w-3 h-3" />,
  [ChunkState.READ]: <CheckCheck className="w-3 h-3 text-status-progress" />,
  [ChunkState.ERROR]: <AlertCircle className="w-3 h-3 text-status-danger" />,
  [ChunkState.FAILED]: <AlertCircle className="w-3 h-3 text-status-danger" />,
  [ChunkState.UNDELIVERED]: <AlertCircle className="w-3 h-3 text-status-danger" />,
}

export const ChunkFooter = ({
  timetoken,
  state,
  className = '',
  align = 'right',
  channel,
  text1,
  stateReason,
}: ChunkFooterProps) => {
  const properTimetoken = getProperTimetoken(timetoken)
  const agentTimezone = getAgentTimezone()
  const formattedTime = dayjs.utc(properTimetoken).tz(agentTimezone).format('MMMM D, YYYY h:mm A')
  const shortTime = dayjs.utc(properTimetoken).tz(agentTimezone).format('h:mm A')

  return (
    <div
      className={cn(
        'flex items-center gap-1 text-xs-400 text-secondary w-full',
        className,
        align === 'right' ? 'justify-end' : 'justify-start',
      )}
    >
      {text1}
      {channel && <ChannelIcon channel={channel} className="size-[16px]" />}
      <div className="flex items-center gap-1 text-xs-400 text-secondary cursor-default">
        <Tooltip content={formattedTime}>
          <span>{shortTime}</span>
        </Tooltip>
        {state && (
          <Tooltip
            // @ts-expect-error - valid element
            content={
              <div className="">
                <span className="capitalize">{state}</span>
                {stateReason && <span className="">:&nbsp;{stateReason}</span>}
              </div>
            }
          >
            <span>{stateIcon[state]}</span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
